import { useEffect, useMemo, useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrEinstellwert, MsrEinstellwerteAntwort } from './typen';
import { herkunftMarke } from './gemeinsam';
import { PdfKnopf } from './PdfKnopf';
import { RANG_ANLAGE, msrZiele, useMsrExport } from './kopf';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState, Feld, FormSection, StatusBadge,
  type Spalte,
} from '../../muster';

/** Der Bereichshinweis „min…max Einheit" — leer, wenn beides fehlt. */
function bereichText(z: Pick<MsrEinstellwert, 'min' | 'max' | 'einheit'>): string {
  if (z.min === undefined && z.max === undefined) return '—';
  return `${z.min ?? '…'}…${z.max ?? '…'}${z.einheit ? ` ${z.einheit}` : ''}`;
}

const HERKUNFT_MARKE = (herkunft: MsrEinstellwert['herkunft']) => (
  <StatusBadge {...herkunftMarke(herkunft)} klein />
);

/**
 * Karte „Einstellwerte" einer Anlage (Spezifikation Abschnitt 14, Vertrag
 * Ergänzung (3)) — Tabelle gruppiert nach Aggregat (Anlagenwerte, `aggregat`
 * fehlt, zuerst), KI-Vorschlag je Anlage; die Excel-Mappe der Anlage steht seit
 * TM-8a in der Exportleiste im Kopf (`useMsrExport`). Wird von
 * `AnlageDetail` (Baum → Anlage) UND `Funktionen` (Reiter GA-Funktionen)
 * benutzt, deshalb eigene Datei wie `Funktionsbeschreibung`.
 *
 * Umzug U6c: Liste (`DataTable`) und Bearbeitung (`Drawer` mit
 * `FormSection`/`Feld`) getrennt. Der Bereich (min…max) und die Schrittweite
 * stehen jetzt AM FELD und nicht mehr als Fließtext daneben — beim Ändern
 * eines Sollwerts ist die Grenze das Wichtigste (Auftrag Abschnitt 15).
 * Speichern verlangt weiterhin eine Begründung; `Herkunft` wird dabei
 * 'gepflegt'.
 */
export function Einstellwerte({ api, projektId, anlageId }: {
  api: ApiClient; projektId: string; anlageId: string;
}) {
  const [antwort, setAntwort] = useState<MsrEinstellwerteAntwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [vorschlagLaeuft, setVorschlagLaeuft] = useState(false);
  const [vorschlagErgebnis, setVorschlagErgebnis] = useState<string | null>(null);

  const [offen, setOffen] = useState<MsrEinstellwert | null>(null);
  const [wert, setWert] = useState('');
  const [begruendung, setBegruendung] = useState('');
  const [speichertLaeuft, setSpeichertLaeuft] = useState(false);
  const [speicherFehler, setSpeicherFehler] = useState<string | null>(null);

  const neuLaden = () => {
    setFehler(null);
    api.msrEinstellwerte(projektId, anlageId).then(setAntwort)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  };

  useEffect(() => {
    setAntwort(null);
    setVorschlagErgebnis(null);
    neuLaden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anlageId]);

  const werteAktualisieren = (neu: MsrEinstellwert) => {
    setAntwort((a) => (a
      ? { ...a, werte: a.werte.map((w) => (w.id === neu.id ? neu : w)) }
      : a));
  };

  const hatKi = (antwort?.werte ?? []).some((w) => w.herkunft === 'ki');

  const bearbeiten = (z: MsrEinstellwert) => {
    setOffen(z);
    setWert(z.wert);
    setBegruendung('');
    setSpeicherFehler(null);
  };

  const speichern = async () => {
    if (!offen) return;
    if (!begruendung.trim()) {
      setSpeicherFehler('Begründung erforderlich.');
      return;
    }
    setSpeichertLaeuft(true);
    setSpeicherFehler(null);
    try {
      const neu = await api.msrEinstellwertSetzen(projektId, offen.id, { wert, begruendung });
      werteAktualisieren(neu);
      setOffen(null);
    } catch (e) {
      setSpeicherFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichertLaeuft(false);
    }
  };

  const vorschlagen = async () => {
    setVorschlagLaeuft(true);
    setVorschlagErgebnis(null);
    setFehler(null);
    try {
      const r = await api.msrEinstellwerteVorschlagen(projektId, anlageId, hatKi);
      setVorschlagErgebnis(
        `${r.erzeugt} erzeugt, ${r.geaendert} geändert, ${r.beibehalten} beibehalten `
        + `(${r.eingabeTokens}/${r.ausgabeTokens} Token, ${r.modell}).`);
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setVorschlagLaeuft(false);
    }
  };

  useMsrExport(RANG_ANLAGE, () => (antwort
    ? [msrZiele.einstellwerteAnlage(api, projektId, { id: anlageId, kennung: antwort.anlage.kennung })]
    : []), [api, projektId, anlageId, antwort?.anlage.kennung]);

  const gruppenSortiert = useMemo(() => {
    const gruppen = new Map<string, MsrEinstellwert[]>();
    for (const w of antwort?.werte ?? []) {
      const schluessel = w.aggregat ?? '';
      const liste = gruppen.get(schluessel) ?? [];
      liste.push(w);
      gruppen.set(schluessel, liste);
    }
    return Array.from(gruppen.entries()).sort(([a], [b]) => {
      if (a === b) return 0;
      if (a === '') return -1;
      if (b === '') return 1;
      return a.localeCompare(b, 'de');
    });
  }, [antwort]);

  const spalten: Spalte<MsrEinstellwert>[] = [
    {
      schluessel: 'schluessel', titel: 'Schlüssel', immerSichtbar: true,
      wert: (z) => z.schluessel,
      inhalt: (z) => <span style={{ fontFamily: 'monospace' }}>{z.schluessel}</span>,
    },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (z) => z.bezeichnung },
    {
      schluessel: 'wert', titel: 'Wert', wert: (z) => z.wert,
      inhalt: (z) => `${z.wert}${z.einheit ? ` ${z.einheit}` : ''}`,
    },
    { schluessel: 'bereich', titel: 'Bereich', sortierbar: false,
      wert: (z) => bereichText(z) },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status', breite: '110px',
      wert: (z) => herkunftMarke(z.herkunft).text,
      inhalt: (z) => HERKUNFT_MARKE(z.herkunft),
    },
    { schluessel: 'begruendung', titel: 'Begründung', wert: (z) => z.begruendung ?? null },
    { schluessel: 'quelle', titel: 'Quelle', wert: (z) => z.quelle ?? null },
    {
      schluessel: 'aktion', titel: 'Ändern', breite: '120px', sortierbar: false,
      wert: () => '',
      inhalt: (z) => (
        <button type="button" className="m-knopf" onClick={() => bearbeiten(z)}>
          Bearbeiten
        </button>
      ),
    },
  ];

  return (
    <ContentSection
      titel="Einstellwerte"
      beschreibung={antwort && !antwort.vorschlagVerfuegbar
        ? 'Kein KI-Dienst eingerichtet — der Vorschlag steht nicht zur Verfügung.'
        : undefined}
      aktionen={(
        <>
          <button type="button" className="m-knopf"
            disabled={vorschlagLaeuft || !antwort || !antwort.vorschlagVerfuegbar}
            onClick={vorschlagen}>
            {vorschlagLaeuft ? 'läuft …'
              : hatKi ? 'KI-Vorschlag erneuern' : 'KI-Vorschlag erzeugen'}
          </button>
          <PdfKnopf api={api} projektId={projektId} art="einstellwerte" ziel="anlage"
            zielId={anlageId} label="Einstellwerte" />
        </>
      )}
    >
      {/* TV11e: die Nutzungszone, nach der die Vorlagen gelten (AKS-Gruppe, Raumnutzung, Standard) - bis TV11e nur in
          der Excel-Titelzeile. Ist die Raumnummer der Raumautomation mehrfach aktiv, sagt der Hinweis warum. */}
      {antwort && (
        <div className="schwach" style={{ marginTop: 8 }}>
          Nutzungszone: <strong>{antwort.anlage.nutzung}</strong>
        </div>
      )}
      {antwort?.anlage.nutzungHinweis && (
        <div className="hinweis warnung" role="status" style={{ marginTop: 8 }}>
          <span className="hinweis-zeichen">!</span> {antwort.anlage.nutzungHinweis}
        </div>
      )}

      {vorschlagErgebnis && (
        <div className="hinweis" style={{ marginTop: 8 }}>{vorschlagErgebnis}</div>
      )}

      {fehler && (
        <ErrorState
          meldung="Die Einstellwerte dieser Anlage konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={neuLaden}
        />
      )}

      {antwort && antwort.werte.length === 0 && (
        <EmptyState
          titel="Für diese Anlage ist kein Einstellwert hinterlegt."
          erklaerung={'Einstellwerte entstehen aus den Vorlagen des Ableitungslaufs — '
            + 'oder aus einem KI-Vorschlag.'}
          aktion={antwort.vorschlagVerfuegbar ? (
            <button type="button" className="m-knopf m-knopf-haupt"
              disabled={vorschlagLaeuft} onClick={vorschlagen}>
              KI-Vorschlag erzeugen
            </button>
          ) : undefined}
        />
      )}

      {!antwort && !fehler && (
        <DataTable
          name="Einstellwerte"
          spalten={spalten}
          zeilen={[]}
          schluessel={(z) => z.id}
          laedt
          dicht
          suche={false}
          spaltenwahl={false}
        />
      )}

      {antwort && antwort.werte.length > 0 && gruppenSortiert.map(([aggregat, zeilen]) => (
        <DataTable
          key={aggregat || '__anlage__'}
          name={aggregat || 'Anlagenwerte'}
          spalten={spalten}
          zeilen={zeilen}
          schluessel={(z) => z.id}
          dicht
          suche={false}
          spaltenwahl={false}
          aktiveZeile={offen?.id}
          kontextmenue={[{ beschriftung: 'Bearbeiten …', beiWahl: bearbeiten }]}
          leer={<EmptyState klein titel="Diese Gruppe führt keinen Einstellwert." />}
        />
      ))}

      <Drawer
        offen={offen !== null}
        titel={offen ? `${offen.schluessel} — ${offen.bezeichnung}` : ''}
        beschreibung={offen
          ? `${offen.aggregat ?? 'Anlagenwert'} · Bereich ${bereichText(offen)}`
            + (offen.schrittweite !== undefined ? ` · Schritt ${offen.schrittweite}` : '')
          : undefined}
        beiSchliessen={() => setOffen(null)}
        fuss={(
          <>
            <button type="button" className="m-knopf m-knopf-haupt"
              disabled={speichertLaeuft || begruendung.trim() === ''}
              onClick={() => void speichern()}>
              {speichertLaeuft ? 'speichert …' : 'Speichern'}
            </button>
            <button type="button" className="m-knopf" onClick={() => setOffen(null)}>
              Abbrechen
            </button>
          </>
        )}
      >
        {offen && (
          <FormSection titel="Einstellwert" spalten={1}>
            <Feld beschriftung="Wert" wert={wert} pflicht
              einheit={offen.einheit ?? undefined}
              hinweis={`Bereich ${bereichText(offen)}`
                + (offen.schrittweite !== undefined
                  ? ` · Schrittweite ${offen.schrittweite}` : '')}
              beiAenderung={(w) => setWert(w as string)} />
            <Feld beschriftung="Begründung" wert={begruendung} pflicht
              hinweis="Ein geänderter Einstellwert ohne Begründung ist nicht prüfbar."
              fehler={begruendung.trim() === '' ? 'Begründung erforderlich.' : undefined}
              beiAenderung={(w) => setBegruendung(w as string)} />
          </FormSection>
        )}

        {speicherFehler && (
          <ErrorState
            klein
            meldung="Der Einstellwert konnte nicht gespeichert werden."
            technisch={speicherFehler}
            beiErneutVersuchen={() => void speichern()}
          />
        )}
      </Drawer>
    </ContentSection>
  );
}
